/**
 * Getting the reset token out of whatever the person pasted.
 *
 * WHY THIS EXISTS: the reset email is built by the backend from its
 * `FRONTEND_URL` setting, which currently points at the API host rather than
 * at this app (`…:8005/reset-password?token=…`, and the staging and production
 * configs name api hosts too). So the link in the mail opens the backend,
 * which has no such page, and the reset dead-ends.
 *
 * The TOKEN is still perfectly good — it is minted against the account, not
 * against the host in the URL. So rather than leave people stuck until the
 * backend setting is fixed, this app accepts the whole link pasted in, or the
 * bare token, and carries on.
 *
 * Pure, so the parsing is tested rather than trusted.
 */

/** The token is opaque; this is only a sanity floor against stray words. */
const LOOKS_LIKE_TOKEN = /^[A-Za-z0-9._~+/=-]{16,512}$/;

/**
 * A pasted link, or a pasted token, or nothing usable.
 *
 * Handles a URL with the token in the query, a bare token, and the common
 * mangling of a link copied out of an email client — surrounding angle
 * brackets, a trailing full stop, wrapped whitespace.
 */
export function tokenFromPaste(text) {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return null;

  /* Whitespace is only ever mail-client line wrapping inside a LINK. A bare
     token never contains any — and collapsing it everywhere would turn
     "I forgot my password" into something token-shaped. */
  const looksLikeLink = /^https?:\/\//i.test(trimmed) || /^[<("']*\/?[^\s]*[?&]token=/i.test(trimmed);
  if (!looksLikeLink && /\s/.test(trimmed)) return null;

  const raw = looksLikeLink ? trimmed.replace(/\s+/g, '') : trimmed;

  /* Strip what mail clients wrap around a link. */
  const cleaned = raw.replace(/^[<("']+/, '').replace(/[>)"'.,;]+$/, '');
  if (!cleaned) return null;

  /* A query string anywhere in it — the host does not matter, and is wrong
     today anyway. */
  const inQuery = cleaned.match(/[?&]token=([^&#\s]+)/i);
  if (inQuery) {
    const value = decodeURIComponent(inQuery[1]);
    return LOOKS_LIKE_TOKEN.test(value) ? value : null;
  }

  /* Some clients drop the query when linkifying; take the last path segment
     of anything that still looks like a URL. */
  if (/^https?:\/\//i.test(cleaned)) {
    const tail = cleaned.split(/[?#]/)[0].split('/').filter(Boolean).pop();
    return tail && LOOKS_LIKE_TOKEN.test(tail) ? tail : null;
  }

  return LOOKS_LIKE_TOKEN.test(cleaned) ? cleaned : null;
}
